import {
    AuthContext,
    CategoryTags,
    ChatPanel,
    ComposePanel,
    CoreFetch,
    FilterMethod,
    IComment,
    ICustomFilter,
    IMessage,
    ITableColumn,
    ITableFetchOptions,
    ListProviderMessagesOrderEnum,
    MessageApi,
    MessageList,
    MessagingProvider,
    ProviderClaim,
    ProviderMessageSortType,
    ToastContext,
} from '@enter/core';
import { DataTableCard, PageLayout, PageLayoutHeader, PageLayoutRow, PageLayoutTitle, useDocumentView } from 'components';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DialogContext } from 'services';
import { IInjectedRootStore, IMessageDetail, IMessageListingRequest } from 'stores';
import * as S from './messaging.styles';

interface IMatchParams {
    messageId: string;
}

type IMessagingProps = IInjectedRootStore;

export const Messaging: React.FunctionComponent<IMessagingProps> = inject('rootStore')(
    observer((props: IMessagingProps) => {
        const { rootStore } = props;
        if (!rootStore) {
            return <></>;
        }
        document.title = 'Provider Dashboard - Messaging';
        const navigate = useNavigate();
        const params = useParams();
        const location = useLocation();

        const authContext = useContext(AuthContext);
        const toastContext = useContext(ToastContext);
        const dialogContext = useContext(DialogContext);
        const [claims, setClaims] = useState<ProviderClaim[] | undefined>();
        const [isDetailLoading, setDetailLoading] = useState<boolean>();
        const [isListLoading, setListLoading] = useState<boolean>();
        const [fetchOptions, setFetchOptions] = useState<ITableFetchOptions>();
        const [messageId, setMessageId] = useState<string | undefined>();
        const [messageDetail, setMessageDetail] = useState<IMessageDetail | undefined>();
        // Block list fetch if there's a messageId - we need to load message detail first
        const [allowListFetch, setAllowListFetch] = useState<boolean>(!params.messageId || params.messageId === 'compose');
        const [openFile] = useDocumentView();
        const isLoadingRef = useRef<string | null>(null);
        const isCancelledRef = useRef<boolean>(false);
        const navigatedAwayFromErrorRef = useRef<boolean>(false);
        const failedMessageIdsRef = useRef<Set<string>>(new Set());
        const hasSetMessagingGroupRef = useRef<boolean>(false);

        const { roleId } = rootStore;
        const isReadOnly: boolean = roleId === 'PROVIDER_READ_ONLY';
        const isReadOnlyClaimChoice: boolean = false;

        // Force MobX to track these observables by accessing them at component level
        const messageStore = rootStore.messageStore;
        const storeInboxMessages = messageStore.inboxMessages;
        const storeInboxTotal = messageStore.inboxTotal;
        const storeDoneMessages = messageStore.doneMessages;
        const storeDoneTotal = messageStore.doneTotal;
        const storeUnreadInboxTotal = messageStore.unreadInboxTotal;

        useEffect(() => {
            return () => {
                if (hasSetMessagingGroupRef.current) {
                    authContext.cancelGroup && authContext.cancelGroup('messaging');
                    hasSetMessagingGroupRef.current = false;
                }
            };
        }, [authContext]);

        useEffect(() => {
            rootStore.messageStore.loadMessageCategories();
            rootStore.messageStore.loadProviderUsers();
        }, [rootStore.messageStore]);

        useEffect(() => {
            const paramsMessageId = params.messageId;
            isCancelledRef.current = false;
            
            const loadMessage = async () => {
                if (paramsMessageId && paramsMessageId !== 'compose') {
                    if (failedMessageIdsRef.current.has(paramsMessageId)) {
                        isCancelledRef.current = true;
                        navigatedAwayFromErrorRef.current = true;
                        isLoadingRef.current = null;
                        hasSetMessagingGroupRef.current = false;
                        setMessageDetail(undefined);
                        setMessageId(undefined);
                        setAllowListFetch(false);
                        navigate('..', { replace: true, relative: 'path' });
                        return;
                    }
                    
                    if (isLoadingRef.current === paramsMessageId) {
                        return;
                    }
                    
                    isLoadingRef.current = paramsMessageId;
                    navigatedAwayFromErrorRef.current = false;
                    setAllowListFetch(false);
                    setMessageId(paramsMessageId);
                    setMessageDetail(undefined);
                    setDetailLoading(true);
                    try {
                        authContext.setGroup && authContext.setGroup('messaging');
                        hasSetMessagingGroupRef.current = true;
                        
                        const detail = await rootStore.messageStore.loadMessageDetail(paramsMessageId, true);
                        
                        if (isCancelledRef.current || isLoadingRef.current !== paramsMessageId) {
                            return;
                        }
                        
                        if (detail) {
                            rootStore.messageStore.markAsRead(paramsMessageId);
                            setMessageDetail(detail);
                            failedMessageIdsRef.current.delete(paramsMessageId);
                            navigatedAwayFromErrorRef.current = false;
                            setAllowListFetch(true);
                        } else {
                            failedMessageIdsRef.current.add(paramsMessageId);
                            hasSetMessagingGroupRef.current = false;
                            setMessageDetail(undefined);
                            setMessageId(undefined);
                            setAllowListFetch(false);
                            isLoadingRef.current = null;
                            navigate('..', { replace: true, relative: 'path' });
                        }
                    } catch (err: any) {
                        if (isCancelledRef.current || isLoadingRef.current !== paramsMessageId) {
                            return;
                        }
                        
                        const isAbortError = 
                            (err instanceof Error && err.name === 'AbortError') ||
                            (err instanceof Error && err.message?.includes('aborted')) ||
                            (typeof err === 'object' && err !== null && 'name' in err && err.name === 'AbortError');
                        
                        if (isAbortError) {
                            setAllowListFetch(false);
                        } else {
                            failedMessageIdsRef.current.add(paramsMessageId);
                            isCancelledRef.current = true;
                            navigatedAwayFromErrorRef.current = true;
                            isLoadingRef.current = null;
                            hasSetMessagingGroupRef.current = false;
                            setMessageDetail(undefined);
                            setMessageId(undefined);
                            setAllowListFetch(false);
                            navigate('..', { replace: true, relative: 'path' });
                        }
                    } finally {
                        if (!isCancelledRef.current && isLoadingRef.current === paramsMessageId) {
                            setDetailLoading(false);
                            isLoadingRef.current = null;
                            hasSetMessagingGroupRef.current = false;
                        }
                    }
                } else {
                    if (!isCancelledRef.current && !navigatedAwayFromErrorRef.current) {
                        isLoadingRef.current = null;
                        setAllowListFetch(true);
                        setMessageId(undefined);
                        setMessageDetail(undefined);
                        setDetailLoading(false);
                    } else if (navigatedAwayFromErrorRef.current) {
                        navigatedAwayFromErrorRef.current = false;
                        isLoadingRef.current = null;
                        setMessageId(undefined);
                        setMessageDetail(undefined);
                        setDetailLoading(false);
                        setAllowListFetch(false);
                    }
                }
            };
            
            loadMessage();
            
            return () => {
                isCancelledRef.current = true;
            };
        }, [params.messageId, rootStore.messageStore, authContext, navigate]);

        useEffect(() => {
            if (allowListFetch && fetchOptions && !isDetailLoading) {
                handleFetchData(fetchOptions);
            }
        }, [allowListFetch, fetchOptions, isDetailLoading]);

        const buildRequest = (showDone: boolean, newFetchData: ITableFetchOptions): IMessageListingRequest => {
            const { pageSize, pageIndex, sortBy, validFilters } = newFetchData;
            const sort: ProviderMessageSortType[] = [];
            const order: ListProviderMessagesOrderEnum[] = [];
            _.forEach(sortBy, rule => {
                let s;
                switch (rule.id) {
                    case 'From':
                        s = ProviderMessageSortType.AUTHOR;
                        break;
                    case 'Subject':
                        s = ProviderMessageSortType.SUBJECT;
                        break;
                    case 'Updated':
                        s = ProviderMessageSortType.UPDATED;
                        break;
                    case 'Purpose':
                        s = ProviderMessageSortType.PURPOSE;
                        break;
                    default:
                        s = undefined;
                }
                if (s) {
                    sort.push(s);
                    order.push(
                        rule.desc ? ListProviderMessagesOrderEnum.DESCENDING : ListProviderMessagesOrderEnum.ASCENDING,
                    );
                }
            });
            const doneFilter: ICustomFilter = {
                filterId: 'filterOpen',
                filterMethod: FilterMethod.EQ,
                filterType: 'String',
                value: showDone ? '1' : '0',
            };
            return {
                page: pageIndex,
                pageSize,
                sort,
                order,
                filters: [...validFilters, doneFilter],
            };
        };

        const refresh = async (): Promise<void> => {
            if (fetchOptions) {
                handleFetchData();
            }
        };

        const handleFetchData = async (newFetchOptions?: ITableFetchOptions): Promise<void> => {
            const options = newFetchOptions || fetchOptions;
            if (!options) {
                return;
            }
            
            if (newFetchOptions) {
                setFetchOptions(newFetchOptions);
            }
            
            if (allowListFetch) {
                try {
                    setListLoading(true);
                    authContext.setGroup && authContext.setGroup('messaging');
                    authContext.cancelGroup && authContext.cancelGroup('messaging');

                    const inboxReq = buildRequest(false, options);
                    const doneReq = buildRequest(true, options);
                    await Promise.all([
                        rootStore.messageStore.loadMessageListing(inboxReq),
                        rootStore.messageStore.loadMessageListing(doneReq),
                    ]);
                    setListLoading(false);
                } catch (err) {
                    const isAbortError = 
                        (err instanceof Error && err.name === 'AbortError') ||
                        (err instanceof Error && err.message?.includes('aborted')) ||
                        (typeof err === 'object' && err !== null && 'name' in err && err.name === 'AbortError');
                    
                    if (!isAbortError) {
                        dialogContext.showError(err);
                    }
                    setListLoading(false);
                }
            }
        };

        const refreshMessageDetail = async (mid: string, forceFetch?: boolean): Promise<void> => {
            if (mid === 'compose') {
                return;
            }
            authContext.setGroup && authContext.setGroup('messaging');
            authContext.cancelGroup && authContext.cancelGroup('messaging');

            const detail = await rootStore.messageStore.loadMessageDetail(mid, forceFetch);
            if (detail && !_.isEqual(detail, messageDetail)) {
                setMessageDetail(detail);
                setMessageId(mid);
                setDetailLoading(false);
            }
        };

        const doSearch = async (term: string): Promise<ProviderClaim[]> => {
            setClaims([]);
            const results = await rootStore.claimStore.loadBasicClaimSearchResults(term);
            setClaims(results);
            return results;
            // return new Promise((resolve, reject) => {
            //     debouncedDoSearch(term, resolve, reject);
            // });
        };

        const debouncedDoSearch = _.debounce(async (term, resolve, reject) => {
            try {
                setClaims([]);
                const results = await rootStore.claimStore.loadBasicClaimSearchResults(term);
                setClaims(results);
                resolve(results);  // Resolve the promise with the results
            } catch (error) {
                reject(error);  // Reject the promise if there's an error
            }
        }, 800);


        const claimSearch = async (term: string): Promise<ProviderClaim[]> => {
            authContext.setGroup && authContext.setGroup('messaging-claim-search');
            authContext.cancelGroup && authContext.cancelGroup('messaging-claim-search');
            return await doSearch(term);
        };
        const composeComment = async ({ messageId: mid, body, files }: IComment): Promise<void> => {
            try {
                await rootStore.messageStore.addComment(rootStore.account, mid, body, files);
                await refreshMessageDetail(mid, true);
            } catch (err) {
                dialogContext.showError(err);
            }
        };
        const composeMessage = async ({ subject, body, claimId, files, links, priority, dueDate }: IMessage): Promise<void> => {
            try {
                // Extract claim IDs from links if available, otherwise use single claimId
                const claimIds = links?.claims?.map(c => c.id).filter(Boolean) || (claimId ? [claimId] : []);
                // Extract category IDs from links if available
                const categoryIds = links?.messageCategories?.map(c => c.id).filter(Boolean);
                // Extract user IDs from links if available
                const assignedUserIds = links?.users?.map(u => u.id).filter(Boolean);
                await rootStore.messageStore.composeMessage(rootStore.account, subject, body, claimIds, files, priority, dueDate, categoryIds, assignedUserIds);
                await refresh();
                toastContext.notify?.('Message sent', 'left');
            } catch (err) {
                dialogContext.showError(err);
            }
        };
        const downloadDocument = async (id: string): Promise<void> => {
            const file = await rootStore.messageStore.embed(id);

            file.url && openFile(file.url);
        };
        const markAsOpen = async (ids: string | string[]): Promise<void> => {
            const promises = _.map(
                _.concat([], ids),
                async (id): Promise<void> => rootStore.messageStore.markAsOpen(id),
            );
            await Promise.all(promises);
            await refresh();
        };
        const markAsClosed = async (ids: string | string[]): Promise<void> => {
            const promises = _.map(
                _.concat([], ids),
                async (id): Promise<void> => rootStore.messageStore.markAsClosed(id),
            );
            await Promise.all(promises);
            await refresh();
        };
        const markAsRead = async (ids: string | string[]): Promise<void> => {
            const promises = _.map(
                _.concat([], ids),
                async (id): Promise<void> => rootStore.messageStore.markAsRead(id),
            );
            await Promise.all(promises);
            await refresh();
        };
        const messageOpen = async (mid: string): Promise<void> => {
            const isCompose = location.pathname.endsWith('/compose');
            const paramsMessageId = params.messageId;
            
            if (isCompose) {
                navigate(`../${mid}`, { relative: 'path' });
            } else if (paramsMessageId) {
                navigate(`../${mid}`, { relative: 'path' });
            } else {
                navigate(mid);
            }
        };
        const messageClose = async (): Promise<void> => {
            navigate('..', { replace: true, relative: 'path' });
        };

        const showCompose = (): void => {
            const paramsMessageId = params.messageId;
            if (paramsMessageId) {
                navigate('../compose', { relative: 'path' });
            } else {
                navigate('compose');
            }
        };

        const renderMessageList = (): React.ReactElement | undefined => {
            const { doneTotal, doneMessages, inboxTotal, inboxMessages, unreadInboxTotal, messageCategories, providerUsers } = rootStore.messageStore;
            
            const _categories = messageCategories;
            const _users = providerUsers;

            const mapMessages = (messages: typeof storeInboxMessages) => messages.map(m => {
                const categoryNames = _.compact(
                    _.map(m.links?.messageCategories, cat => messageStore.getCategoryName(cat.id || ''))
                );
                const assigneeNames = _.compact(
                    _.map(m.links?.users, user => rootStore.messageStore.getUserName(user.id || ''))
                );
                return {
                    ...m,
                    comments: m.commentCount,
                    documents: m.documentCount,
                    categories: categoryNames,
                    assignees: assigneeNames,
                };
            });

            const customColumns: ITableColumn[] = [
                {
                    id: 'Categories',
                    Header: 'Categories',
                    accessor: 'categories',
                    Cell: ({ cell }: any): React.ReactElement => <CategoryTags categories={cell.value} />,
                    minWidth: 150,
                    maxWidth: 250,
                    disableSortBy: true,
                    show: true,
                },
                {
                    id: 'Assignee',
                    Header: 'Assignee',
                    accessor: 'assignees',
                    Cell: ({ cell }: any): React.ReactElement => <CategoryTags categories={cell.value} />,
                    minWidth: 150,
                    maxWidth: 250,
                    disableSortBy: true,
                    show: true,
                },
            ];

            return (
                <>
                    <PageLayout>
                        <PageLayoutRow>
                            <PageLayoutHeader>
                                <PageLayoutTitle>Messages</PageLayoutTitle>
                            </PageLayoutHeader>
                        </PageLayoutRow>
                        <PageLayoutRow>
                            <DataTableCard size="large">
                                <S.Wrapper>
                                    <MessageList
                                        doneCount={storeDoneTotal}
                                        doneMessages={mapMessages(storeDoneMessages)}
                                        inboxCount={storeInboxTotal}
                                        inboxMessages={mapMessages(storeInboxMessages)}
                                        unreadCount={storeUnreadInboxTotal}
                                        onFetchData={handleFetchData}
                                        onShowCompose={showCompose}
                                        showFiltering={true}
                                        showNavigation={true}
                                        showSettings={true}
                                        selectedMessageId={messageId}
                                        customColumns={customColumns}
                                    />
                                </S.Wrapper>
                            </DataTableCard>
                        </PageLayoutRow>
                    </PageLayout>
                </>
            );
        };

        const renderComposePanel = (): React.ReactElement | undefined => {
            const state = window.history.state?.usr || {};
            const claimId = state.claimId;
            const subject = state.subject;
            const body = state.body;
            if (!isReadOnly) {
                return (
                    <S.ChatPanelContainer>
                        <ComposePanel claimId={claimId} subject={subject} body={body} hideNote={true} className="fixed" />
                    </S.ChatPanelContainer>
                );
            }
        };

        const paramsMessageId = params.messageId;
        const isComposePath = location.pathname.endsWith('/compose');

        const linkEntityToMessage = async (id: string, entityType: string, entityIds: string[]): Promise<void> => {
            try {
                await CoreFetch(MessageApi).linkProviderEntityToMessage({
                    id,
                    linkEntityToMessageRequest: { entityType, entityIds }
                });
                setClaims([]);
                await refreshMessageDetail(id, true);
            } catch (error) {
                throw error;
            }
        };

        const unlinkEntityFromMessage = async (id: string, entityType: string, entityIds: string[]): Promise<void> => {
            try {
                await CoreFetch(MessageApi).unlinkProviderEntityToMessage({
                    id,
                    linkEntityToMessageRequest: { entityType, entityIds }
                });
                setClaims([]);
                await refreshMessageDetail(id, true);
            } catch (error) {
                throw error;
            }
        };

        return (
            <MessagingProvider
                isReadOnlyClaimChoice={isReadOnlyClaimChoice}
                isReadOnly={isReadOnly}
                isListLoading={isListLoading}
                isDetailLoading={isDetailLoading}
                account={rootStore.account}
                enableMessageTypeValidation={true}
                message={messageDetail ? messageDetail.message : undefined}
                comments={messageDetail ? messageDetail.comments : undefined}
                documents={messageDetail ? messageDetail.documents : undefined}
                claims={claims}
                getCategoryName={rootStore.messageStore.getCategoryName}
                getUserName={rootStore.messageStore.getUserName}
                onClaimSearch={claimSearch}
                onComposeComment={composeComment}
                onComposeMessage={composeMessage}
                onDocumentDownload={downloadDocument}
                onMarkAsClosed={markAsClosed}
                onMarkAsOpen={markAsOpen}
                onMarkAsRead={markAsRead}
                onMessageClose={messageClose}
                onMessageOpen={messageOpen}
                onLinkEntityToMessage={linkEntityToMessage}
                onUnlinkEntityFromMessage={unlinkEntityFromMessage}>
                {renderMessageList()}
                {isComposePath && renderComposePanel()}
                {paramsMessageId && !isComposePath && (
                    <S.ChatPanelContainer>
                        <ChatPanelImpl onBack={() => navigate('..', { replace: true, relative: 'path' })} />
                    </S.ChatPanelContainer>
                )}
            </MessagingProvider>
        );
    }),
);

const ChatPanelImpl: React.FC<{ 
    onBack: () => void;
}> = ({ onBack }) => {
    return <ChatPanel hideNote={true} className="fixed" onBack={onBack} />;
};
